import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Paper,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  Article as ArticleIcon,
  CloudUpload as CloudUploadIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const EnLandingPage = ({ onLanguageChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: <TimelineIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Timeline View',
      description: 'Organize conversations chronologically and quickly find past information using date and tag filters.',
    },
    {
      icon: <SearchIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Keyword Search',
      description: 'Search within conversation content with keyword highlighting for visual confirmation of matches.',
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Favorites Management',
      description: 'Mark important conversations as favorites and manage them in a dedicated view.',
    },
    {
      icon: <ArticleIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Markdown Display',
      description: 'Beautiful rendering of markdown conversations with proper display of code blocks and lists.',
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Data Import',
      description: 'Easily import conversation data from JSON files with support for batch processing.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Local Storage',
      description: 'Privacy-focused local browser storage. Your data is never sent to external servers.',
    },
  ];

  const handleGetStarted = () => {
    navigate('/app?lang=en');
  };

  const handleSwitchToJapanese = () => {
    onLanguageChange('ja');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Helmet>
        <html lang="en" />
        <title>SynaptiQore - Transform AI Conversations into Knowledge</title>
        <meta name="description" content="A knowledge tool to organize, search, and reuse insights from AI conversations." />
        <link rel="canonical" href="https://synaptiqore.contextaflex.com/?lang=en" />
        <meta property="og:title" content="SynaptiQore - Transform AI Conversations into Knowledge" />
        <meta property="og:description" content="A knowledge tool to organize, search, and reuse insights from AI conversations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://synaptiqore.contextaflex.com/?lang=en" />
        <meta property="og:image" content="https://synaptiqore.contextaflex.com/logo512.png" />
        <meta property="og:site_name" content="SynaptiQore" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SynaptiQore - Transform AI Conversations into Knowledge" />
        <meta name="twitter:description" content="A knowledge tool to organize, search, and reuse insights from AI conversations." />
        <meta name="twitter:image" content="https://synaptiqore.contextaflex.com/logo512.png" />
        <link rel="alternate" hrefLang="ja" href="https://synaptiqore.contextaflex.com/" />
        <link rel="alternate" hrefLang="en" href="https://synaptiqore.contextaflex.com/?lang=en" />
      </Helmet>
      {/* Hero section: clean white background */}
      <Box sx={{ position: 'relative', backgroundColor: 'background.default', borderBottom: `1px solid ${theme.palette.divider}` }}>
        {/* Language Switch Button */}
        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSwitchToJapanese}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            日本語
          </Button>
        </Box>

        <Container maxWidth="lg">
          <Box sx={{ pt: 8, pb: 2, textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                mb: 1,
                fontSize: { xs: '2.5rem', md: '3.2rem' },
              }}
            >
              SynaptiQore
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                mb: 1,
              }}
            >
              Synaptiqore
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: '500',
                mb: 4,
              }}
            >
              Transform Knowledge Connections into Core Insights
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main hero section: white background for content focus */}
      <Box sx={{ backgroundColor: 'background.default' }}>
        {/* Hero Section */}
        <Container maxWidth="lg">
        <Box sx={{ py: { xs: 6, md: 7 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 'bold',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Transform AI Conversations
                <br />
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.primary.main,
                    px: 2,
                    py: 0.8,
                    borderRadius: 2,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    display: 'inline-block',
                  }}
                >
                  into Knowledge
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                A knowledge tool to organize, search, and reuse insights from conversations
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    backgroundImage: theme.custom?.gradient,
                    color: 'white',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    boxShadow: 3,
                    '&:hover': {
                      opacity: 0.9,
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  Learn More
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: 400,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <ArticleIcon sx={{ fontSize: 120, color: alpha(theme.palette.primary.main, 0.4) }} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
      </Box>
      {/* Features Section: light surface for contrast */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 8 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 6,
              }}
            >
              Key Features
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Box sx={{ mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 'bold',
                          mb: 2,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* CTA Section: light surface for readability */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Box component={Paper} sx={{ p: 6, borderRadius: 4, border: `1px solid ${theme.palette.divider}` }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 'bold',
                  mb: 3,
                }}
              >
                Ready to Transform Your Conversations?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Start organizing your AI conversations and unlock the knowledge within them.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  backgroundImage: theme.custom?.gradient,
                  color: 'white',
                  fontWeight: 'bold',
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  boxShadow: 2,
                  '&:hover': {
                    opacity: 0.95,
                  },
                }}
              >
                Get Started Now
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer (light background) */}
      <Container maxWidth="lg">
        <Box sx={{ py: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            © 2024 SynaptiQore. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default EnLandingPage; 